import React, { useState } from 'react';
import axios from 'axios';
import './App.css';

function App() {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [expandedUrls, setExpandedUrls] = useState({});
  const [progress, setProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [activeTab, setActiveTab] = useState({});
  const [dotCount, setDotCount] = useState(1);
  const [expandedBuilders, setExpandedBuilders] = useState({});

  const handleAnalyze = async (e) => {
    e.preventDefault();

    if (!url.trim()) {
      setError('Please enter a URL');
      return;
    }

    setLoading(true);
    setError('');
    setResults(null);
    setExpandedUrls({});
    setProgress(5);
    setLoadingMessage('Initializing crawler');
    setDotCount(1);

    // Animated dots interval
    const dotInterval = setInterval(() => {
      setDotCount(prev => (prev % 3) + 1);
    }, 500); // Cycle dots every 500ms

    // Progress simulation messages
    const progressSteps = [
      { progress: 15, message: 'Fetching website content' },
      { progress: 25, message: 'Discovering internal pages' },
      { progress: 35, message: 'Crawling website pages' },
      { progress: 50, message: 'Analyzing HTML structure' },
      { progress: 65, message: 'Detecting form builders' },
      { progress: 75, message: 'Identifying forms' },
      { progress: 85, message: 'Extracting form details' },
      { progress: 92, message: 'Processing results' },
      { progress: 97, message: 'Finalizing analysis' }
    ];

    // Start progress simulation
    let currentStep = 0;
    const progressInterval = setInterval(() => {
      if (currentStep < progressSteps.length) {
        setProgress(progressSteps[currentStep].progress);
        setLoadingMessage(progressSteps[currentStep].message);
        currentStep++;
      }
    }, 12000); // Update every 12 seconds

    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
const response = await axios.post(`${apiUrl}/api/analyze`, {
  url: url.trim()
}, {
  timeout: 300000 // 5 minute timeout (matches server timeout)
});

      clearInterval(progressInterval);
      clearInterval(dotInterval);
      setProgress(100);
      setLoadingMessage('Complete!');

      if (response.data.success) {
        setResults(response.data);
      } else {
        setError(response.data.error || 'Failed to analyze URL');
      }
    } catch (err) {
      clearInterval(progressInterval);
      clearInterval(dotInterval);

      let errorMessage = 'Failed to analyze URL';

      if (err.code === 'ECONNABORTED') {
        errorMessage = 'Analysis timed out. The website may be too large or slow to respond. Try a smaller website or try again later.';
      } else if (err.response?.data?.details) {
        errorMessage = err.response.data.details;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.response?.status >= 500) {
        // Avoid exposing raw HTTP 500 messages like "Request failed with status code 500"
        errorMessage = 'The server encountered an error while analyzing this URL. Please try again later.';
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setTimeout(() => {
        setLoading(false);
        setProgress(0);
        setLoadingMessage('');
      }, 500);
    }
  };

  const toggleUrlExpansion = (formIndex) => {
    setExpandedUrls(prev => ({
      ...prev,
      [formIndex]: !prev[formIndex]
    }));
  };

  const toggleBuilderExpansion = (builder) => {
    setExpandedBuilders(prev => ({
      ...prev,
      [builder]: !prev[builder]
    }));
  };

  const setFormTab = (formIndex, tab) => {
    setActiveTab(prev => ({
      ...prev,
      [formIndex]: tab
    }));
  };

  const getActiveTab = (formIndex) => {
    return activeTab[formIndex] || 'details';
  };

  const generateGTMListener = (form) => {
    // Map form builders to their specific listeners from reference file
    const builder = form.builder;

    // Form builder specific listeners (keep in sync with GTM_Form_Listeners_Reference.txt)
    const listeners = {
      'Contact Form 7': `document.addEventListener('wpcf7mailsent', function(event) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    'event': 'cf7submission',
    'formId': event.detail.contactFormId,
    'response': event.detail.inputs
  });
});`,

'Gravity Forms': `jQuery(document).ready(function() {
  jQuery(document).bind("gform_confirmation_loaded", function(event, formID) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'event': 'formSubmission',
      'formID': formID
    });
  });
});`,

      'WPForms': `document.addEventListener('DOMContentLoaded', function() {
  var elementsArray = document.querySelectorAll('[id^="wpforms-form-"]');
  elementsArray.forEach(function(elem) {
    elem.addEventListener('submit', function(event) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        'event': 'wpFormSubmit',
        'wpFormElement': event.target
      });
    });
  });
});`,

      'Ninja Forms': `jQuery(function($) {
  $(document).on('nfFormSubmitResponse', function(e, response) {
    var formId = response.response.data.form_id;
    var formName = response.response.data.settings.title;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'event': 'ninja_form_submit',
      'formId': formId,
      'formName': formName
    });
  });
});`,

      'Formidable Forms': `// Formidable Forms uses Element Visibility Trigger in GTM
// Setup in GTM:
// 1. Create new trigger: Element Visibility
// 2. Selection Method: CSS Selector
// 3. Element Selector: .frm_message
// 4. When to fire: Once Per Page
// 5. Minimum Percent Visible: 1%
// Then create GA4/Analytics tag that fires on this trigger`,

      'Elementor Forms': `window.dataLayer = window.dataLayer || [];
document.addEventListener('submit_success', function(event) {
  var formElement = event.target.closest('form');
  var formId = formElement ? formElement.getAttribute('data-form-id') : '';

  window.dataLayer.push({
    'event': 'elementor_form_submit',
    'formId': formId
  });
});`,

      'HubSpot Forms': `window.dataLayer = window.dataLayer || [];

// Listener for older version (v3) of HubSpot forms
window.addEventListener('message', function(event) {
  if (event.data.type === 'hsFormCallback' && event.data.eventName === 'onFormSubmitted') {
    window.dataLayer.push({
      'event': 'form_submission',
      'form_id': event.data.id,
      'conversion_id': event.data.data.conversionId,
      'form_data': event.data.data.submissionValues
    });
  }
});

// Listener for v4 HubSpot forms
window.addEventListener('hs-form-event:on-submission:success', function(event) {
  var hsform = HubspotFormsV4.getFormFromEvent(event);
  if (hsform) {
    hsform.getFormFieldValues().then(function(fieldValues) {
      var transformedData = fieldValues.reduce(function(obj, item) {
        var key = item.name.split('/')[1];
        if (key) {
          obj[key] = item.value;
        }
        return obj;
      }, {});

      window.dataLayer.push({
        'event': 'form_submission',
        'form_id': hsform.getFormId(),
        'conversion_id': hsform.getConversionId(),
        'form_data': transformedData
      });
    });
  }
});`,

      'Typeform': `window.addEventListener('message', function(event) {
  if (event.origin === 'https://form.typeform.com' && (event.data.type === 'form-started' || event.data.type === 'form-submit')) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'event': event.data.type.replace('-', '_'),
      'form_id': event.data.formId
    });
  }
});`,

      'Jotform': `// Works for both iFrame embeds and source code implementations
window.addEventListener('message', function(event) {
  if (event.origin.includes('jotform.com') && event.data.action === 'submission-completed') {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'event': 'jotform_submit',
      'formId': event.data.formID
    });
  }
});

// For source code forms
document.addEventListener('submit', function(event) {
  var form = event.target.closest('form[action*="jotform.com"]');
  if (form) {
    var formData = new FormData(form);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'event': 'jotform_submit',
      'formId': formData.get('formID')
    });
  }
});`,

      'Mailchimp': `// Track successful subscriptions (most important for conversions)
mc4wp.forms.on('subscribed', function(form) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    'event': 'mailchimp_subscribe',
    'formId': form.id,
    'formName': form.name
  });
});

// Optional: Track form errors
mc4wp.forms.on('error', function(form) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    'event': 'mailchimp_error',
    'formId': form.id
  });
});`
    };

    // Return builder-specific listener or generic one
    return listeners[builder] || `document.addEventListener('submit', function(event) {
  var form = event.target;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    'event': 'form_submission'
  });
});`;
  };

  const getListenerSource = (form) => {
    const sources = {
      'Contact Form 7': {
        label: 'Analytics Mania',
        url: 'https://www.analyticsmania.com/post/contact-form-7-event-tracking-with-google-tag-manager/'
      },
'Gravity Forms': {
        label: 'Analytics Mania',
        url: 'https://www.analyticsmania.com/post/track-gravity-forms-with-google-tag-manager/'
      },
      'WPForms': {
        label: 'Slider Rule Tech',
        url: 'https://slideruletech.com/posts/capture-wpforms-in-gtm/'
      },
      'Ninja Forms': {
        label: 'MeasureSchool',
        url: 'https://measureschool.com/ninja-forms-tracking/'
      },
      'Formidable Forms': {
        label: 'Formidable Forms',
        url: 'https://formidableforms.com/knowledgebase/how-can-i-set-up-google-analytics-with-my-forms/'
      },
      'Elementor Forms': {
        label: 'DumbData',
        url: 'https://dumbdata.co/listeners/elementor-form/'
      },
      'HubSpot Forms': {
        label: 'Analytics Mania',
        url: 'https://www.analyticsmania.com/post/track-hubspot-forms-with-google-tag-manager-and-google-analytics-4/'
      },
      'Typeform': {
        label: 'Analytics Mania',
        url: 'https://www.analyticsmania.com/post/track-typeform-with-google-tag-manager-and-google-analytics-4/'
      },
      'Jotform': {
        label: 'Pilot Digital',
        url: 'https://pilotdigital.com/blog/jotform-tracking-in-ga4-with-google-tag-manager/'
      },
      'Mailchimp': {
        label: 'DumbData',
        url: 'https://dumbdata.co/listeners/mailchimp-for-wordpress-form/'
      }
    };

    return sources[form.builder] || null;
  };

  const copyToClipboard = async (text, formIndex) => {
    try {
      await navigator.clipboard.writeText(text);
      // You could add a toast notification here
      alert('Copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy:', err);
      alert('Failed to copy to clipboard');
    }
  };

  return (
    <div className="App">
      <div className="container">
        <header className="header">
          <h1>Website Form Detector</h1>
          <p>Crawl entire websites to detect and analyze all forms</p>
        </header>

        <form onSubmit={handleAnalyze} className="search-form">
          <div className="input-group">
            <input
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="Enter website URL (e.g., example.com)"
              className="url-input"
              disabled={loading}
            />
            <button type="submit" className="analyze-btn" disabled={loading}>
              {loading ? 'Analyzing...' : 'Analyze'}
            </button>
          </div>
          {error && <div className="error-message">{error}</div>}
        </form>

        {loading && (
          <div className="loading-container">
            <div className="progress-bar-container">
              <div className="progress-bar-header">
                <span className="loading-message">{loadingMessage}{'.'.repeat(dotCount)}</span>
                <span className="progress-percentage">{progress}%</span>
              </div>
              <div className="progress-bar-wrapper">
                <div
                  className="progress-bar-fill"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <div className="estimated-time">
                Estimated time: ~2-3 minutes
              </div>
            </div>
          </div>
        )}

        {results && (
          <div className="results">
            <div className="summary">
              <div className="summary-item">
                <span className="summary-number">{results.totalForms}</span>
                <span className="summary-label">Unique Forms</span>
              </div>
              <div className="summary-item">
                <span className="summary-number">{results.totalPages}</span>
                <span className="summary-label">Pages Analyzed</span>
              </div>
            </div>

            {results.builderSummary && Object.keys(results.builderSummary).length > 0 && (
              <div className="builder-summary-section">
                <h2>Forms by Builder</h2>
                <div className="builder-summary-grid">
                  {Object.entries(results.builderSummary).map(([builder, data]) => {
                    const isExpanded = expandedBuilders[builder] || false;
                    const formsToShow = isExpanded ? data.forms : data.forms.slice(0, 3);
                    const remainingCount = data.forms.length - 3;

                    return (
                      <div key={builder} className="builder-summary-card">
                        <div className="builder-header">
                          <h3>{builder}</h3>
                          <span className="builder-count">{data.count} form{data.count !== 1 ? 's' : ''}</span>
                        </div>
                        <ul className="builder-forms-list">
                          {formsToShow.map((form, idx) => {
                            // Find the index of this form in the main forms array
                            const formIndex = results.forms.findIndex(f => f.id === form.id && f.name === form.name);
                            return (
                              <li key={idx}>
                                <a href={`#form-${formIndex}`} className="form-link">
                                  <span className="form-name">{form.name}</span>
                                  <span className="form-id-small">ID: {form.id}</span>
                                  <span className="form-location-small">{form.location}</span>
                                  <span className="form-pages-small">{form.occurrences} page{form.occurrences !== 1 ? 's' : ''}</span>
                                </a>
                              </li>
                            );
                          })}
                        </ul>
                        {data.forms.length > 3 && (
                          <button
                            className="show-more-btn"
                            onClick={() => toggleBuilderExpansion(builder)}
                          >
                            {isExpanded ? 'Show less' : `Show ${remainingCount} more`}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="result-section">
              <h2>Detailed Form Information</h2>
              {results.forms.length === 0 ? (
                <p className="no-results">No forms detected on this website</p>
              ) : (
                <div className="forms-list">
                  {results.forms.map((form, index) => (
                    <div key={index} id={`form-${index}`} className="form-card">
                      <div className="form-header">
                        <div className="form-title">
                          <h3>{form.name}</h3>
                          <span className="form-builder">{form.builder}</span>
                        </div>
                        <span className="form-id">ID: {form.id}</span>
                      </div>

                      <div className="form-tabs">
                        <button
                          className={`tab-btn ${getActiveTab(index) === 'details' ? 'active' : ''}`}
                          onClick={() => setFormTab(index, 'details')}
                        >
                          Details & HTML Form Listener
                        </button>
                        <button
                          className={`tab-btn ${getActiveTab(index) === 'urls' ? 'active' : ''}`}
                          onClick={() => setFormTab(index, 'urls')}
                        >
                          URLs ({form.urls.length})
                        </button>
                      </div>

                      <div className="form-body">
                        {getActiveTab(index) === 'details' && (
                          <>
                            <div className="form-info">
                              <div className="info-row">
                                <span className="info-label">Location:</span>
                                <span className="info-value">{form.location}</span>
                              </div>
                              <div className="info-row">
                                <span className="info-label">Field Count:</span>
                                <span className="info-value">{form.fieldCount} fields</span>
                              </div>
                              <div className="info-row">
                                <span className="info-label">Found on:</span>
                                <span className="info-value">{form.urls.length} page{form.urls.length !== 1 ? 's' : ''}</span>
                              </div>
                            </div>

                            <div className="url-examples">
                              <strong>Example URLs:</strong>
                              <ul className="url-list-small">
                                {form.urls.slice(0, 2).map((url, i) => (
                                  <li key={i}>
                                    <a href={url} target="_blank" rel="noopener noreferrer">{url}</a>
                                  </li>
                                ))}
                              </ul>
                              {form.urls.length > 2 && (
                                <span className="see-all-urls" onClick={() => setFormTab(index, 'urls')}>
                                  See all {form.urls.length} URLs →
                                </span>
                              )}
                            </div>

                            <div className="gtm-code-section">
                              <div className="gtm-header">
                                <strong>HTML Form Submission Listener</strong>
                                <button
                                  className="copy-btn"
                                  onClick={() => copyToClipboard(generateGTMListener(form), index)}
                                >
                                  Copy
                                </button>
                              </div>
                              <pre className="gtm-code">
                                <code>{generateGTMListener(form)}</code>
                              </pre>
                              {getListenerSource(form) && (
                                <div className="gtm-source">
                                  <span>Source: </span>
                                  <a
                                    href={getListenerSource(form).url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {getListenerSource(form).label}
                                  </a>
                                </div>
                              )}
                            </div>
                          </>
                        )}

                        {getActiveTab(index) === 'urls' && (
                          <div className="form-urls">
                            <strong>All URLs ({form.urls.length}):</strong>
                            <ul className="url-list">
                              {form.urls.map((url, i) => (
                                <li key={i}>
                                  <a href={url} target="_blank" rel="noopener noreferrer">{url}</a>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
